/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import {CreditItem} from "types/entities/PackagesItems";
import clsx from "clsx";
import FeatherIcon from "components/FeatherIcon";
import {currencyNumberFormat, currencyNumberFormatNoDigit} from "utils/utility";
import {pricePerCredits} from "utils/creditsUtils";
import {usePackageItemKartingOptions} from "utils/packageUtils";

interface BookingPackagesCreditsSelectFamilyProps {
    sortedPackageCreditItems: CreditItem[];
    selectedPackageCreditItem: CreditItem | null;
    handlePackageCreditItemChange: (item: CreditItem | null) => void;
}

/**
 * Family credit package selector.
 * Shows family-category credit options with labels.
 * Selecting a package applies it to the whole family (assigned to the first adult).
 */
const BookingPackagesCreditsSelectFamily = ({
    sortedPackageCreditItems,
    selectedPackageCreditItem,
    handlePackageCreditItemChange,
}: BookingPackagesCreditsSelectFamilyProps) => {
    const {t} = useTranslation();

    const smallPackages = sortedPackageCreditItems.filter(item => (item.article?.creditPackage?.amount_total ?? 0) < 96);
    const largePackages = sortedPackageCreditItems.filter(item => (item.article?.creditPackage?.amount_total ?? 0) >= 96);
    const {getRecommendedCreditPackageItem} = usePackageItemKartingOptions();
    const renderItem = (item: CreditItem) => {
        const isSelected = selectedPackageCreditItem?.id === item.id;
        const isRecommended = getRecommendedCreditPackageItem(item);

        return (
            <div key={item.id} className={"flex flex-col w-full md:w-[170px] lg:w-[160px] xl:w-[170px]"}>
                {isRecommended && (
                    <div
                        className={
                            "rounded-t-xl flex flex-row w-full items-center justify-center gap-2 px-2 py-1.5 uppercase bg-[linear-gradient(90deg,#FFAE00_0%,#E1570B_35%,#D12C11_70%,#C20016_100%)] text-white"
                        }
                    >
                        <img src="/img/credits/top-recommendation-icon.svg" alt="top recommendation" className="size-[12px]" />
                        <span className={"text-white text-[10px] font-bold leading-none"}>{t("Top recommendation")}</span>
                    </div>
                )}
                <button
                    type={"button"}
                    onClick={() => handlePackageCreditItemChange(item)}
                    className={clsx(
                        "min-h-[74px] sm:min-h-[80px] md:min-h-[96px] w-full flex flex-col items-center justify-center border-2 transition-all duration-150",
                        isRecommended ? "rounded-b-xl border-t-0" : "rounded-xl",
                        isSelected ? "bg-arbo-red/5 border-arbo-red" : "border-[#E5E5E5] bg-white hover:border-arbo-red"
                    )}
                >
                    <div className={"flex flex-row items-center gap-2 lg:gap-3 px-2 py-2.5 w-full"}>
                        <div className={"border border-[#E5E5E5] rounded-full bg-white p-1"}>
                            <FeatherIcon
                                name={"check"}
                                className={clsx("size-3 xs:size-4 sm:size-5 flex-none", isSelected ? "text-arbo-red" : "text-transparent")}
                                strokeWidth={3}
                            />
                        </div>
                        <div className={"flex flex-row md:flex-col gap-1 w-full md:w-auto items-center md:items-start"}>
                            <span className={clsx("font-bold text-xs sm:text-sm lg:text-base flex-1 text-left", isSelected ? "text-arbo-red" : "text-black")}>
                                {item.article?.creditPackage?.name ?? t("Unknown")}
                            </span>
                            <span className={clsx("font-semibold text-[11px] xs:text-xs lg:text-sm shrink-0", isSelected ? "text-arbo-red" : "text-black")}>
                                {currencyNumberFormat(item.price)}
                            </span>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className={"w-full border-t border-solid border-[#E5E5E5]"} />

                    {/* price */}
                    <div className={"text-[10px] font-medium w-full text-center pt-1.5 pb-1"}>
                        {t("Price per credit")} {currencyNumberFormat(pricePerCredits(item.article?.creditPackage?.amount_total ?? 0))}
                    </div>
                </button>
            </div>
        );
    };

    return (
        <div
            className={
                "BookingPackagesCreditsSelectFamily grid grid-cols-1 xs:grid-cols-2 md:grid md:grid-cols-[auto_auto_auto_auto] gap-2 w-full items-end justify-center"
            }
        >
            {/* No Credits option */}
            <button
                type={"button"}
                onClick={() => handlePackageCreditItemChange(null)}
                className={clsx(
                    "min-h-[74px] sm:min-h-[80px] md:min-h-[96px] w-full md:w-[122px] lg:w-[100px] xl:w-[122px] flex flex-row items-center justify-center gap-2 md:gap-3 rounded-xl border-2 px-2 sm:px-4 lg:px-2 py-3 transition-all duration-150",
                    selectedPackageCreditItem === null
                        ? "bg-[#D9D9D9] border-mid-grey text-mid-grey border-solid"
                        : "border-[#E5E5E5] bg-white hover:border-arbo-red border-dashed"
                )}
            >
                <div className={"flex flex-col gap-1 md:gap-0 w-full md:w-auto items-center"}>
                    <span
                        className={clsx(
                            "font-bold text-xs sm:text-sm lg:text-base flex-1 text-left",
                            selectedPackageCreditItem === null ? "text-mid-grey" : "text-gray-800"
                        )}
                    >
                        {t("None")}
                    </span>
                    <span
                        className={clsx(
                            "font-semibold text-[11px] xs:text-xs lg:text-sm shrink-0",
                            selectedPackageCreditItem === null ? "text-mid-grey" : "text-gray-800"
                        )}
                    >
                        {currencyNumberFormatNoDigit(0)}
                    </span>
                </div>
            </button>

            {largePackages.map(renderItem)}

            {/* Smaller packages label */}
            {smallPackages.length > 0 && (
                <div
                    className={
                        "min-h-[74px] sm:min-h-[80px] md:min-h-[96px] w-full md:w-[122px] lg:w-[100px] xl:w-[122px] flex flex-col items-center justify-center gap-0 py-3 md:col-start-1"
                    }
                >
                    <span className={"font-semibold text-xs sm:text-sm text-gray-800 text-center leading-tight"}>{t("Smaller")}</span>
                    <span className={"font-semibold text-xs sm:text-sm text-gray-800 text-center leading-tight"}>{t("Packages")}</span>
                </div>
            )}

            {smallPackages.map(renderItem)}
        </div>
    );
};

export default BookingPackagesCreditsSelectFamily;
